import Grid from "@mui/material/Grid2";
import { FormGroup } from "@cofano/cck";
import { intl } from "LocaleWrapper";
import { DataFormat } from "utils";
import { PayableOver, AgreementTypeEnum } from "__generated__/graphql";
import { List, ListItem, ListItemText } from "@mui/material";
import { CalculationMethod } from "view/calculationmethods/types/calculationmethod";
import { Fragment, ReactNode } from "react";
import { Session } from "ezStore";
import { CalculationMethodDetailsByTypeProps } from "@schemas/calculationmethods/schema";
import { CurrencyFragment } from "__generated__/graphql/fragments.generated";

type DetailsField = {
	id: string;
	render: () => ReactNode;
}

type WeightRangesProps = {
	calculationMethod: CalculationMethod;
	currency: CurrencyFragment;
	session: Session;
}

const CargoQuantityCalculationMethodDetails = ({ calculationMethod, currency, session, agreementType, hiddenFields }: CalculationMethodDetailsByTypeProps) => {
	const CargoQuantityCalculationMethodDetailsFields: DetailsField[] = [
		{
			id: "calculationMethod.weightRanges",
			render: () => agreementType === AgreementTypeEnum.WeightRange && (
				<WeightRangesDetails
					calculationMethod={calculationMethod}
					currency={currency}
					session={session} />
			),
		},
		{
			id: "calculationMethod.price",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: `PRICE_PER_${calculationMethod.cargoQuantityUnit}` })} overflow="hidden">
						{DataFormat.price(calculationMethod.price, currency.isoCode, session.locale)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.payableOver",
			render: () => calculationMethod.payableOver && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "PAYABLE_OVER" })} overflow="hidden">
						{intl.formatMessage({ id: `PAYABLE_OVER_${calculationMethod.payableOver}` })}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.preAgreed",
			render: () => calculationMethod.payableOver === PayableOver.Preagreed && (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "ORDER_QUANTITY" })} overflow="hidden">
						{DataFormat.tonnage(calculationMethod.preAgreed)}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumPrice",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "MINIMUM_PRICE" })} overflow="hidden">
						{calculationMethod.minimumPrice ? DataFormat.price(calculationMethod.minimumPrice, currency.isoCode, session.locale) : "-"}
					</FormGroup>
				</Grid>
			),
		},
		{
			id: "calculationMethod.minimumQuantityPayable",
			render: () => (
				<Grid size={4}>
					<FormGroup label={intl.formatMessage({ id: "MINIMUM_QUANTITY_PAYABLE" })}>
						{calculationMethod.minimumQuantityPayable ? DataFormat.tonnage(calculationMethod.minimumQuantityPayable) : "-"}
					</FormGroup>
				</Grid>
			),
		},
	];

	return (
		<>
			{CargoQuantityCalculationMethodDetailsFields
				.filter(x => !hiddenFields?.includes(x.id))
				.map(x => (
					<Fragment key={x.id}>
						{x.render()}
					</Fragment>
				))
			}
		</>
	);
};

const WeightRangesDetails = ({ calculationMethod, session, currency }: WeightRangesProps) => (
	<Grid size={12}>
		<FormGroup label={intl.formatMessage({ id: "RANGE" })} overflow="hidden">
			<List dense style={{ padding: 0 }}>
				{calculationMethod.weightRanges?.map((w, i) => (
					<ListItem key={i}>
						<ListItemText
							primary={(
								<>
									<span style={{ display: "inline-flex", fontSize: 13, marginRight: 8, minWidth: 90 }}>
										{`${w.from} - ${w.to}`}
									</span>

									<span style={{ fontSize: 13 }}>
										{DataFormat.price(w.price, currency.isoCode, session.locale)}
										{" / "}
										{intl.formatMessage({ id: "UNIT_TON" })}
									</span>
								</>
							)} />
					</ListItem>
				))}
			</List>
		</FormGroup>
	</Grid>
);

export default CargoQuantityCalculationMethodDetails;
